/**
 * AM-1921 — Doters points and referenced (store) payments cannot be combined.
 *
 * IAMSA/CITEC rejects a `FormasPago` that carries a points record on a referenced payment:
 *
 * ```
 * 0|100|0000|No es posible confirmar el pago referenciado.|0||IDA=0,REG=0||0|
 * ```
 *
 * That error is the provider's intended behaviour, not a defect on our side. Their reasoning:
 * a referenced payment produces a *reservation*, not a confirmed sale — nobody knows yet
 * whether the buyer will pay the reference before it expires — while redeeming points
 * corresponds to a certain sale. Reported by IAMSA on 2026-09-04 and reproduced three times in
 * the GHO sandbox (points record identical to the card path, and with the referenced-payment
 * envelope: rejected either way; the same purchase without points completes and issues
 * tickets).
 *
 * So the combination is withdrawn from the buyer instead of being sent and refused. Before
 * this, the flow let the buyer redeem points, pick OXXO, and only then fail at the very last
 * step, with the points already committed and no reference to pay.
 *
 * **Scope is deliberately narrow.** Only the `doters` wallet, and only the two referenced
 * payments CITEC treats as a reservation (`conekta`/OXXO and `paycash`). Other wallets
 * (`travelpass`, `costapass`) and other cash rails (`efecty`, Kushki's `store`) belong to
 * different providers and there is no evidence they share the restriction — widening this
 * would remove payment methods from brands that accept them today.
 *
 * **And it is opt-in per brand,** through the `DOTERS_REFERENCED_PAYMENT_DISABLED` whitelabel
 * feature flag. Withdrawing a payment method is visible to the buyer, so a brand needs to align
 * its own site copy and terms before it is turned on; GFA and Roll & Bits share this wallet and
 * this cash rail and would otherwise be changed without notice. Absent flag ⇒ falsy ⇒ today's
 * behaviour, unchanged.
 *
 * The naming follows the sibling flag `DOTERS_OXXO_PAYMENT_WITH_FLAT_FARE_DISABLED`, which
 * already exists and renders the *notice* about this very restriction in `DotersRewardsSwitch`
 * — the copy was there, the enforcement was not.
 *
 * **QuickCheckout is out of scope, and can be.** `PaymentPanel.js:88-101` hands
 * `purchase.availablePayments` / `purchase.paymentMethods` to `PaymentOptions` raw, so nothing
 * here reaches that flow — the same pre-existing limitation `bulkAplazoAvailability` has. It is
 * not a hole, because the combination cannot be created there: `QuickCheckout.js` has no wallet
 * surface at all (no `wallet`/`doters`/`points` in its 842 lines) and `CheckoutWallet`, the
 * redemption widget, is mounted only by `Checkout.js`. Points are applied on the checkout screen
 * or not at all. If a redemption ever lands in QuickCheckout, this gate has to go with it.
 */

/**
 * The `available_payments` strings for the referenced payments CITEC treats as a reservation.
 *
 * These are the legacy representation the backend still emits alongside `payment_methods`
 * (`purchase_detail_serializer.rb#available_payments`). Kushki's `'store'` and `'efecty'` are
 * intentionally absent — see the module note on scope.
 * @type {string[]}
 */
export const REFERENCED_AVAILABLE_PAYMENTS = Object.freeze(['oxxo', 'paycash']);

/**
 * The provider/engine markers whose `type: 'store'` methods are referenced payments for CITEC.
 *
 * `purchase_detail_serializer.rb#payment_methods` emits
 * `{ provider: 'oxxo', engine: 'conekta', type: 'store' }` and
 * `{ provider: 'paycash', engine: 'paycash', type: 'store' }`, so `conekta` and `paycash` are
 * the engines that decide the offline branch on the integration side (`createReserveRequest.js`
 * routes exactly those two through `createOfflineFixture`).
 *
 * `oxxo` is included as well because the shape is not consistent across the codebase: the
 * bulk-Aplazo suite fixtures an OXXO method as `{ type: 'store', provider: 'oxxo', engine:
 * 'oxxo' }`. Matching either field keeps the filter working whichever way the entry arrives,
 * and stays narrow: Kushki's cash rail carries neither marker.
 * @type {string[]}
 */
export const REFERENCED_PAYMENT_MARKERS = Object.freeze(['oxxo', 'conekta', 'paycash']);

/**
 * Whether a `payment_methods` entry is a referenced (store) payment CITEC treats as a
 * reservation.
 * @param {object} [method] - A payment method entry.
 * @returns {boolean} True for a store method on a referenced-payment engine or provider.
 */
export const isReferencedStoreMethod = (method) =>
  method?.type === 'store' &&
  (REFERENCED_PAYMENT_MARKERS.includes(method?.engine) ||
    REFERENCED_PAYMENT_MARKERS.includes(method?.provider));

/**
 * Whether a legacy `available_payments` string is a referenced payment.
 * @param {string} [payment] - An `available_payments` entry.
 * @returns {boolean} True for `oxxo` and `paycash`.
 */
const isReferencedAvailablePayment = (payment) => REFERENCED_AVAILABLE_PAYMENTS.includes(payment);

/**
 * Whether a value reads as a positive amount of redeemed points.
 *
 * The wallet fields arrive as numbers but the purchase payload has delivered them as strings
 * before, so they are coerced. `null`, `undefined`, `''` and anything non-numeric are not a
 * redemption: a field that simply has not loaded must never withdraw a payment method.
 * @param {number|string} [value] - A wallet amount or point count.
 * @returns {boolean} True when the value is a positive number.
 */
const isPositiveAmount = (value) => {
  if (value === null || value === undefined || value === '') return false;

  const amount = Number(value);

  return Number.isFinite(amount) && amount > 0;
};

/**
 * Whether this purchase has Doters points committed to it.
 *
 * Any of the five fields counts. The checkout sets `walletPointsUsed` when the buyer moves the
 * slider, `walletMinimumPointsToBeUsed` on a flat-fare redemption, and `walletBalanceUsed`
 * carries the resulting currency amount.
 *
 * The `walletTotal*` pair is not redundant. `wallet_minimum_points_to_be_used` returns 0 unless
 * four further conditions hold (`app/services/doters/wallet.rb:110-115` in platform-purchase):
 * `flat_fare?`, `doters_met_business_rules?`, the Doters user among the passengers (or flexible
 * redeem), and `flat_fare_wallet_points_required` still matching the last flat-fare movement.
 * `wallet_total_points_used` sums the movement without any of them. So a flat fare redeemed and
 * then disturbed — the service level or the ancillaries change and the required points stop
 * matching the recorded movement — leaves the first three fields at 0 while the points record
 * still exists. That is precisely the case the backend does not cover either, since it gates
 * `'oxxo'`/`'paycash'` on `wallet_balance_used`.
 *
 * @param {object} params - Parameters.
 * @param {string} [params.walletType] - The loyalty wallet in play (`doters`, `travelpass`…).
 * @param {number} [params.walletPointsUsed] - Points the buyer chose to redeem.
 * @param {number} [params.walletBalanceUsed] - Currency amount those points cover.
 * @param {number} [params.walletMinimumPointsToBeUsed] - Points a flat-fare redemption requires.
 * @param {number} [params.walletTotalPointsUsed] - Points across every movement of the purchase.
 * @param {number} [params.walletTotalBalanceUsed] - Currency amount across every movement.
 * @returns {boolean} True when Doters points are committed to this purchase.
 */
export const hasDotersPointsApplied = ({
  walletType,
  walletPointsUsed,
  walletBalanceUsed,
  walletMinimumPointsToBeUsed,
  walletTotalPointsUsed,
  walletTotalBalanceUsed,
} = {}) =>
  walletType === 'doters' &&
  (isPositiveAmount(walletPointsUsed) ||
    isPositiveAmount(walletBalanceUsed) ||
    isPositiveAmount(walletMinimumPointsToBeUsed) ||
    isPositiveAmount(walletTotalPointsUsed) ||
    isPositiveAmount(walletTotalBalanceUsed));

/**
 * Whether this purchase is about to be submitted as a referenced payment with Doters points
 * committed to it — the combination CITEC rejects.
 *
 * The render-time withdrawal cannot close this by itself: the selection lives in redux and
 * survives the method disappearing from the list (see the note on
 * {@link getPaymentsWithoutReferencedForDoters}), and `paymentButtonClick` can be re-entered
 * programmatically, not only by the rendered button. Same reason the Addi document check is
 * repeated at submit time.
 *
 * ⚠️ The branch is on the method's **usable identity**, not on its truthiness: in this app
 * `selectedPaymentMethod` is never falsy. `CheckoutContainer.js:375` dispatches
 * `selectPaymentMethod(method || {})` and `config/defaultEnvs.js:10` seeds
 * `defaultPaymentMethod: {}`, so an empty object reaches here as truthy, `isReferencedStoreMethod({})`
 * is `false`, and a truthiness branch would make the legacy clause unreachable. That is not
 * hypothetical: `Checkout.js:333` calls `setPaymentType(availablePayments[0] || 'credit_card')`
 * over the **raw** `purchase.availablePayments` and with no method object, leaving
 * `selectedPaymentOption: 'oxxo'` alongside `selectedPaymentMethod: {}`.
 *
 * For the same reason the legacy clause accepts the `available_payments` strings and not only
 * `'store'`: `'oxxo'`/`'paycash'` is exactly what that call assigns.
 *
 * With a usable method object present the provider/engine decides, so another provider's cash
 * rail on `type: 'store'` is not caught.
 *
 * @param {object} params - Parameters.
 * @param {boolean} [params.isRestrictionEnabled] - The brand's
 *   `DOTERS_REFERENCED_PAYMENT_DISABLED` feature flag. Falsy ⇒ never blocks.
 * @param {object} [params.selectedPaymentMethod] - The method selected in the store.
 * @param {string} [params.selectedPaymentOption] - The legacy selected option.
 * @param {string} [params.walletType] - The loyalty wallet in play.
 * @param {number} [params.walletPointsUsed] - Points the buyer chose to redeem.
 * @param {number} [params.walletBalanceUsed] - Currency amount those points cover.
 * @param {number} [params.walletMinimumPointsToBeUsed] - Points a flat-fare redemption requires.
 * @param {number} [params.walletTotalPointsUsed] - Points across every movement of the purchase.
 * @param {number} [params.walletTotalBalanceUsed] - Currency amount across every movement.
 * @returns {boolean} True when the payment must not be submitted.
 */
export const isDotersPointsOnReferencedPayment = ({
  isRestrictionEnabled,
  selectedPaymentMethod,
  selectedPaymentOption,
  ...wallet
} = {}) => {
  if (!isRestrictionEnabled) return false;
  if (!hasDotersPointsApplied(wallet)) return false;

  if (selectedPaymentMethod?.type || selectedPaymentMethod?.provider) {
    return isReferencedStoreMethod(selectedPaymentMethod);
  }

  return (
    selectedPaymentOption === 'store' ||
    REFERENCED_AVAILABLE_PAYMENTS.includes(selectedPaymentOption)
  );
};

/**
 * The payment methods to offer, with the referenced payments withdrawn when Doters points are
 * committed to the purchase.
 *
 * Operates on both representations, and returns the **same array references** it received
 * whenever there is nothing to withdraw — no points, another wallet, or no referenced payment
 * offered in the first place — so that path is a verifiable no-op downstream.
 *
 * Derived at render time rather than written to the store, for the same reason as
 * {@link module:utils/purchase/bulkAplazoAvailability}: mutating the backend's response in
 * shared state would reach every consumer instead of just the selector UI.
 *
 * Withdrawing covers the buyer who redeems first: the store methods are gone before they can be
 * picked.
 *
 * ⚠️ It does **not** cover the opposite order on its own. Picking the store method first leaves
 * the selection in redux, and `PaymentSelector`'s re-selection only runs in the `else if` branch
 * (`PaymentSelector.js:470-492`). With `PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED` on, the
 * first branch wins and is one-shot via `hasInitializedRef` (`:461-462`, never reset), so the
 * re-selection never happens — and that flag is on in ETN production alongside Doters. That
 * order is closed by {@link isDotersPointsOnReferencedPayment} at submit time instead.
 *
 * @param {object} params - Parameters. Every wallet field of
 *   {@link hasDotersPointsApplied} is forwarded to it as given.
 * @param {boolean} [params.isRestrictionEnabled] - The brand's
 *   `DOTERS_REFERENCED_PAYMENT_DISABLED` feature flag. Falsy ⇒ the lists are returned untouched.
 * @param {string[]} [params.availablePayments] - The purchase's legacy `available_payments` list.
 * @param {object[]} [params.paymentMethods] - The purchase's `payment_methods` list.
 * @returns {{availablePayments: string[], paymentMethods: object[]}} The lists to render.
 */
export const getPaymentsWithoutReferencedForDoters = ({
  isRestrictionEnabled,
  availablePayments = [],
  paymentMethods = [],
  ...wallet
}) => {
  if (!isRestrictionEnabled || !hasDotersPointsApplied(wallet)) {
    return { availablePayments, paymentMethods };
  }

  const carriesReferencedPayment = availablePayments.some(isReferencedAvailablePayment);
  const carriesReferencedMethod = paymentMethods.some(isReferencedStoreMethod);

  return {
    availablePayments: carriesReferencedPayment
      ? availablePayments.filter((payment) => !isReferencedAvailablePayment(payment))
      : availablePayments,
    paymentMethods: carriesReferencedMethod
      ? paymentMethods.filter((method) => !isReferencedStoreMethod(method))
      : paymentMethods,
  };
};
